import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/lib/apiClient';
import {
  ArrowRight,
  MessageSquare,
  FileText,
  Mail,
  Bot,
  CheckSquare,
  Send,
  Share2,
  type LucideIcon,
} from 'lucide-react';

interface TimelineEvent {
  eventType: string;
  timestamp: string | null;
  actorId: string | null;
  summary: string;
  detail: string | null;
  metadata: Record<string, string | null> | null;
}

const EVENT_ICONS: Record<string, { icon: LucideIcon; color: string }> = {
  STAGE_CHANGE: { icon: ArrowRight, color: 'text-blue-500' },
  COMMENT: { icon: MessageSquare, color: 'text-emerald-500' },
  DOCUMENT: { icon: FileText, color: 'text-amber-500' },
  PROPOSAL: { icon: Send, color: 'text-purple-500' },
  EMAIL: { icon: Mail, color: 'text-cyan-500' },
  AGENT_OUTPUT: { icon: Bot, color: 'text-pink-500' },
  TASK: { icon: CheckSquare, color: 'text-orange-500' },
  // 114.6 — Demo share activity events
  DEMO_SHARED: { icon: Share2, color: 'text-violet-500' },
};

interface Props {
  entityType: 'opportunity' | 'account';
  entityId: string;
}

export function TimelinePanel({ entityType, entityId }: Props) {
  const { t } = useTranslation();
  const [events, setEvents] = useState<TimelineEvent[]>([]);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  useEffect(() => {
    const path =
      entityType === 'opportunity'
        ? `/opportunities/${entityId}/timeline`
        : `/accounts/${entityId}/timeline`;
    apiClient
      .get<TimelineEvent[]>(path)
      .then(({ data }) => setEvents(data))
      .catch(() => setEvents([]));
  }, [entityType, entityId]);

  function toggle(idx: number) {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(idx)) next.delete(idx);
      else next.add(idx);
      return next;
    });
  }

  if (events.length === 0) return null;

  return (
    <div className="rounded-lg border bg-card p-6">
      <h2 className="mb-4 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Timeline
      </h2>
      <div className="relative space-y-0">
        <div className="absolute left-[15px] top-0 bottom-0 w-px bg-border" />
        {events.map((event, idx) => {
          const config = EVENT_ICONS[event.eventType] ?? EVENT_ICONS.TASK;
          const Icon = config.icon;
          const isExpanded = expanded.has(idx);

          return (
            <div
              key={idx}
              className="relative flex gap-3 py-3 pl-8"
            >
              <div
                className={`absolute left-[8px] top-4 flex h-4 w-4 items-center justify-center rounded-full bg-card ${config.color}`}
              >
                <Icon className="h-3 w-3" />
              </div>
              <div className="min-w-0 flex-1">
                <button
                  onClick={() => event.detail ? toggle(idx) : undefined}
                  className="text-left w-full"
                  type="button"
                >
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-medium text-muted-foreground">
                      {event.eventType.replace('_', ' ')}
                    </span>
                    {event.timestamp && (
                      <span className="text-[10px] text-muted-foreground">
                        {new Date(event.timestamp).toLocaleString('en-US', {
                          month: 'short',
                          day: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </span>
                    )}
                  </div>
                  <p className="text-sm">
                    {event.eventType === 'DEMO_SHARED' && event.metadata
                      ? t(
                          `activity.event.demoShared.${event.metadata['channel'] === 'whatsapp' ? 'whatsapp' : 'email'}`,
                          {
                            demoName: event.metadata['demoSlug'] ?? 'demo',
                            recipient: event.metadata['recipient'] ?? '',
                          },
                        )
                      : event.summary}
                  </p>
                </button>
                {isExpanded && event.detail && (
                  <div className="mt-1 rounded-md bg-muted/50 p-2 text-xs text-muted-foreground">
                    {event.detail}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
